import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable, type Column } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  PlusCircle,
  Pencil,
  Loader2,
  CheckCircle2,
  XCircle,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { leaveService } from '@/services/leave.service';
import { toast } from 'sonner';
import type { LeaveType, LeaveRule } from '@pms/shared';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

const PRESET_COLORS = [
  '#0D9488', '#3B82F6', '#8B5CF6', '#F59E0B', '#10B981',
  '#EF4444', '#EC4899', '#F97316', '#6366F1', '#14B8A6',
];

interface MergedRow {
  type: LeaveType;
  rule: LeaveRule | undefined;
}

interface FormState {
  name: string;
  category: 'normal' | 'earned' | 'special';
  is_paid: boolean;
  color: string;
  description: string;
  monthly_credit: number;
  paid_hours_per_day: number;
  allow_half_day: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  category: 'normal',
  is_paid: true,
  color: '#0D9488',
  description: '',
  monthly_credit: 1,
  paid_hours_per_day: 8,
  allow_half_day: false,
};

function CategoryBadge({ category }: { category: LeaveType['category'] }) {
  const map = {
    normal:  'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800',
    earned:  'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
    special: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${map[category]}`}>
      {category}
    </span>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <Switch checked={checked} onChange={onChange} size="sm" aria-label={label} />
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-2 pb-1 border-b border-border/40 mb-3">
      {children}
    </p>
  );
}

export function LeaveTypesMergedSection() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<LeaveType | null>(null);
  const [editingRule, setEditingRule] = useState<LeaveRule | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [pendingToggle, setPendingToggle] = useState<{ id: string; name: string; next: boolean } | null>(null);

  const typesQuery = useQuery({
    queryKey: ['leave', 'types'],
    queryFn: () => leaveService.listLeaveTypes(),
  });

  const rulesQuery = useQuery({
    queryKey: ['leave', 'rules'],
    queryFn: () => leaveService.listLeaveRules(),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const newType = await leaveService.createLeaveType({
        name: form.name,
        category: form.category,
        is_paid: form.is_paid,
        color: form.color,
        description: form.description || undefined,
      });
      try {
        await leaveService.createLeaveRule({
          leave_type_id: newType.id,
          name: `${form.name} Rule`,
          monthly_credit: form.monthly_credit,
          paid_hours_per_day: form.paid_hours_per_day,
          allow_half_day: form.allow_half_day,
          // Every year starts fresh, so nothing is carried forward
          allow_carry_forward: false,
        });
      } catch {
        try {
          await leaveService.deleteLeaveType(newType.id);
        } catch {
          throw new Error('Leave rule creation failed and rollback also failed. Please delete the orphaned leave type manually.');
        }
        throw new Error('Failed to save leave credits. Leave type creation rolled back.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'types'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'rules'] });
      toast.success('Leave type created');
      handleClose();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { message?: string; response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ??
        (err as { message?: string })?.message ??
        'Failed to create leave type';
      toast.error(msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      await leaveService.updateLeaveType(editingType!.id, {
        name: form.name,
        category: form.category,
        is_paid: form.is_paid,
        color: form.color,
        description: form.description || undefined,
      });
      if (editingRule) {
        await leaveService.updateLeaveRule(editingRule.id, {
          name: `${form.name} Rule`,
          monthly_credit: form.monthly_credit,
          paid_hours_per_day: form.paid_hours_per_day,
          allow_half_day: form.allow_half_day,
        });
      } else {
        await leaveService.createLeaveRule({
          leave_type_id: editingType!.id,
          name: `${form.name} Rule`,
          monthly_credit: form.monthly_credit,
          paid_hours_per_day: form.paid_hours_per_day,
          allow_half_day: form.allow_half_day,
          allow_carry_forward: false,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'types'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'rules'] });
      toast.success('Leave type updated');
      handleClose();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ??
        (err as { message?: string })?.message ??
        'Failed to update leave type';
      toast.error(msg);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      leaveService.updateLeaveType(id, { is_active }),
    onSuccess: (_, { is_active }) => {
      queryClient.invalidateQueries({ queryKey: ['leave', 'types'] });
      queryClient.invalidateQueries({ queryKey: ['leave', 'rules'] });
      toast.success(is_active ? 'Leave type activated' : 'Leave type deactivated');
    },
    onError: () => toast.error('Failed to update leave type'),
  });

  function openCreate() {
    setEditingType(null);
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setDialogOpen(true);
  }

  function openEdit(row: MergedRow) {
    setEditingType(row.type);
    setEditingRule(row.rule ?? null);
    setForm({
      name: row.type.name,
      category: row.type.category,
      is_paid: row.type.is_paid,
      color: row.type.color,
      description: row.type.description ?? '',
      monthly_credit: Number(row.rule?.monthly_credit ?? 1),
      paid_hours_per_day: Number(row.rule?.paid_hours_per_day ?? 8),
      allow_half_day: row.rule?.allow_half_day ?? false,
    });
    setErrors({});
    setDialogOpen(true);
  }

  function handleClose() {
    setDialogOpen(false);
    setEditingType(null);
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setErrors({});
  }

  function validate(): boolean {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!/^#[0-9A-Fa-f]{6}$/.test(form.color)) e.color = 'Invalid hex color';
    if (form.monthly_credit < 0) e.monthly_credit = 'Must be ≥ 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    if (editingType) updateMutation.mutate();
    else createMutation.mutate();
  }

  const types = typesQuery.data ?? [];
  const rules = rulesQuery.data ?? [];
  const rows: MergedRow[] = types.map((t) => ({
    type: t,
    rule: rules.find((r) => r.leave_type_id === t.id),
  }));

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const columns: Column<MergedRow>[] = [
    {
      key: 'name',
      header: 'Leave Type',
      cell: (row) => (
        <div className="flex items-center gap-2.5">
          <span
            className="w-3 h-3 rounded-full shrink-0 border border-black/10"
            style={{ backgroundColor: row.type.color }}
          />
          <div>
            <span className="font-medium">{row.type.name}</span>
            {row.type.description && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-xs">{row.type.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      cell: (row) => <CategoryBadge category={row.type.category} />,
    },
    {
      key: 'is_paid',
      header: 'Paid',
      cell: (row) =>
        row.type.is_paid ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        ) : (
          <XCircle className="w-4 h-4 text-muted-foreground" />
        ),
    },
    {
      key: 'credits',
      header: 'Credits / mo',
      cell: (row) =>
        row.rule ? (
          <span className="font-medium">{row.rule.monthly_credit}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'half_day',
      header: 'Half Day',
      cell: (row) =>
        row.rule?.allow_half_day ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        ) : (
          <XCircle className="w-4 h-4 text-muted-foreground" />
        ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <span
          className={
            row.type.is_active
              ? 'inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
          }
        >
          {row.type.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24 text-right',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" onClick={() => openEdit(row)} aria-label="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            disabled={toggleActiveMutation.isPending}
            onClick={() => {
              const next = !row.type.is_active;
              setPendingToggle({ id: row.type.id, name: row.type.name, next });
            }}
            aria-label={row.type.is_active ? 'Deactivate' : 'Activate'}
            className={row.type.is_active ? 'text-muted-foreground hover:text-destructive' : 'text-muted-foreground hover:text-emerald-600'}
          >
            {row.type.is_active
              ? <ToggleRight className="h-5 w-5 text-emerald-500" />
              : <ToggleLeft className="h-5 w-5" />}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate}>
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Leave Type
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.type.id}
        loading={typesQuery.isLoading || rulesQuery.isLoading}
        error={typesQuery.isError || rulesQuery.isError}
        onRetry={() => { void typesQuery.refetch(); void rulesQuery.refetch(); }}
        empty="No leave types yet. Click 'Add Leave Type' to get started."
      />

      <ConfirmDialog
        open={!!pendingToggle}
        onOpenChange={(o) => { if (!o) setPendingToggle(null); }}
        title={`${pendingToggle?.next ? 'Activate' : 'Deactivate'} "${pendingToggle?.name}"?`}
        description={pendingToggle?.next ? 'The leave type will become available for requests.' : 'The leave type will be hidden from new leave requests.'}
        confirmLabel={pendingToggle?.next ? 'Activate' : 'Deactivate'}
        destructive={!pendingToggle?.next}
        onConfirm={() => { if (pendingToggle) toggleActiveMutation.mutate({ id: pendingToggle.id, is_active: pendingToggle.next }); }}
      />

      <Dialog open={dialogOpen} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Leave Type' : 'Add Leave Type'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-1 pt-2">
            {/* ── Basic Info ── */}
            <SectionLabel>Basic Info</SectionLabel>
            <div className="grid gap-4 md:grid-cols-2 pb-2">
              <div className="md:col-span-2">
                <Label className="text-sm font-medium mb-1.5 block">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Casual Leave"
                  className={errors.name ? 'border-red-400' : ''}
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label className="text-sm font-medium mb-1.5 block">Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm((f) => ({ ...f, category: v as FormState['category'] }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="earned">Earned</SelectItem>
                    <SelectItem value="special">Special</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-1.5 block">Type</Label>
                <Select
                  value={form.is_paid ? 'paid' : 'unpaid'}
                  onValueChange={(v) => setForm((f) => ({ ...f, is_paid: v === 'paid' }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label className="text-sm font-medium mb-2 block">Color</Label>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2 flex-wrap">
                    {PRESET_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, color: c }))}
                        className={`w-6 h-6 rounded-full transition-transform ${
                          form.color === c ? 'ring-2 ring-offset-1 ring-offset-background ring-foreground scale-110' : ''
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="w-8 h-8 rounded cursor-pointer border border-border"
                  />
                </div>
                {errors.color && <p className="text-xs text-red-500 mt-1">{errors.color}</p>}
              </div>

              <div className="md:col-span-2">
                <Label className="text-sm font-medium mb-1.5 block">
                  Description <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of this leave type..."
                  rows={2}
                  className="resize-none text-sm"
                  maxLength={300}
                />
              </div>
            </div>

            {/* ── Credits & Hours ── */}
            <SectionLabel>Credits &amp; Hours</SectionLabel>
            <div className="grid gap-4 md:grid-cols-2 pb-2">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Monthly Credits</Label>
                <Input
                  type="number"
                  min={0}
                  max={31}
                  step={0.5}
                  value={form.monthly_credit}
                  onChange={(e) => setForm((f) => ({ ...f, monthly_credit: Number(e.target.value) }))}
                  className={errors.monthly_credit ? 'border-red-400' : ''}
                />
                {errors.monthly_credit && <p className="text-xs text-red-500 mt-1">{errors.monthly_credit}</p>}
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Paid Hrs / Day</Label>
                <Input
                  type="number"
                  min={0}
                  max={24}
                  step={0.5}
                  value={form.paid_hours_per_day}
                  onChange={(e) => setForm((f) => ({ ...f, paid_hours_per_day: Number(e.target.value) }))}
                />
              </div>
            </div>

            {/* ── Options ── */}
            <SectionLabel>Options</SectionLabel>
            <div className="space-y-3 pb-2">
              <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Allow Half Day</p>
                  <p className="text-xs text-muted-foreground">Employee can apply for half-day leave</p>
                </div>
                <Toggle
                  checked={form.allow_half_day}
                  onChange={(v) => setForm((f) => ({ ...f, allow_half_day: v }))}
                  label="Allow half day"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-3">
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSaving}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingType ? (
                  'Save Changes'
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
